import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, updateItem } from '../fitness/fitnessApi';

const LIST = 'SortieRuns';

function safeParse(json) {
  try { return JSON.parse(json) || {}; } catch { return {}; }
}

export function useSortieRuns(getToken, userEmail) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRuns = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const items = await getItems(token, LIST);
      setRuns(
        items
          .filter(it => (it.RunBy || '').toLowerCase() === userEmail.toLowerCase())
          .map(mapRun)
          .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, userEmail]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const launchSortie = useCallback(async (procedure) => {
    const token = await getToken();
    const progress = {};
    procedure.steps.forEach((_, i) => {
      progress[`step_${i}`] = { done: false };
    });
    const now = new Date().toISOString();
    const created = await createItem(token, LIST, {
      Title: `${procedure.name} — ${new Date().toLocaleDateString()}`,
      ProcedureName: procedure.name,
      ProcedureId: procedure.id,
      StartDate: now,
      CompletedDate: '',
      Status: 'Active',
      RunBy: userEmail,
      StepProgress: JSON.stringify(progress),
    });
    await fetchRuns();
    return created;
  }, [getToken, userEmail, fetchRuns]);

  const toggleStep = useCallback(async (runId, stepIndex) => {
    const run = runs.find(r => r.id === runId);
    if (!run) return;
    const token = await getToken();
    const key = `step_${stepIndex}`;
    const progress = { ...run.stepProgress };
    const wasDone = progress[key]?.done;
    progress[key] = wasDone
      ? { done: false }
      : { done: true, at: new Date().toISOString() };

    // Optimistic update
    setRuns(prev => prev.map(r => r.id === runId ? { ...r, stepProgress: progress } : r));

    await updateItem(token, LIST, runId, { StepProgress: JSON.stringify(progress) });
  }, [getToken, runs]);

  const completeRun = useCallback(async (runId) => {
    const token = await getToken();
    await updateItem(token, LIST, runId, {
      Status: 'Complete',
      CompletedDate: new Date().toISOString(),
    });
    await fetchRuns();
  }, [getToken, fetchRuns]);

  const abandonRun = useCallback(async (runId) => {
    const token = await getToken();
    await updateItem(token, LIST, runId, { Status: 'Abandoned' });
    await fetchRuns();
  }, [getToken, fetchRuns]);

  function getActiveRun(procedureId) {
    return runs.find(r => r.procedureId === procedureId && r.status === 'Active') || null;
  }

  function getRunHistory(procedureId) {
    return runs.filter(r => r.procedureId === procedureId);
  }

  return {
    runs, loading, error,
    launchSortie, toggleStep, completeRun, abandonRun,
    getActiveRun, getRunHistory, refetch: fetchRuns,
  };
}

function mapRun(it) {
  return {
    id: it.id,
    procedureName: it.ProcedureName || '',
    procedureId: it.ProcedureId || '',
    startDate: it.StartDate || '',
    completedDate: it.CompletedDate || '',
    status: it.Status || 'Active',
    runBy: it.RunBy || '',
    stepProgress: safeParse(it.StepProgress),
  };
}
